import React from "react";
import PropTypes from "prop-types";
import FormTextField from "components/form/form-textfield";
import FormImageField from "components/form/form-imagefield";
import FormVideoField from "components/form/form-videofield";
import { injectIntl, FormattedMessage } from "react-intl";
import { DataGridItem } from "components/data-grid";
import Select, { AssetCategorySelect } from "components/select";
import { useAssetFormTypeContext } from "../form-provider";
import DataGrid from "../../../../components/data-grid/data-grid";

const NewAssetForm = ({
  acceptVideo = ["video/mp4"],
  acceptImage = ["image/png", "image/apng"],
  allowedAssetTypes = ["image", "video"],
  disabled = false,
  intl,
  values,
  onChange,
  onImageChange,
  path = "",
  defaultCategories,
  disabledCategorySelection,
}) => {
  const { type, setType } = useAssetFormTypeContext();

  const handleImageChange = name => value => {
    onChange(`${path}codename`)(value.name.split(".")[0]);
    onImageChange(name)(value);
  };

  const handleVideoChange = name => value => {
    onChange(`${path}codename`)(value.name.split(".")[0]);
    onChange(name)(value);
  };

  return (
    <DataGrid>
      <DataGridItem big>
        <AssetCategorySelect
          id={`${path}categoryIds`}
          name={`${path}categoryIds`}
          disabled={disabledCategorySelection}
          isInsideForm
          label={intl.formatMessage({ id: "common.asset.selectCategory" })}
          onChange={onChange}
          multiple
          value={values ? values.categoryIds : null}
          defaultCategories={defaultCategories}
        />
      </DataGridItem>
      <DataGridItem big>
        <Select
          disabled={disabled}
          id={`${path}type`}
          name={`${path}type`}
          value={type}
          onChange={event => {
            setType(event.target.value);
            onChange(`${path}image`)("");
            onChange(`${path}codename`)("");
          }}
          getLabelFromElement={type => intl.formatMessage({ id: `asset.type.in.${type}` })}
          elements={allowedAssetTypes}
        />
        {type === "image" && acceptImage.includes("image/apng") && <FormattedMessage id="asset.type.animation.size" />}
      </DataGridItem>
      <DataGridItem big>
        {type === "video" ? (
          <FormVideoField
            accept={acceptVideo.join()}
            disabled={disabled}
            id={`${path}image`}
            name={`${path}image`}
            label={intl.formatMessage({ id: "common.asset.selectFile.label" })}
            onChange={handleVideoChange}
            fileForPreview={values.image}
          />
        ) : (
          <FormImageField
            accept={acceptImage.join()}
            disabled={disabled}
            id={`${path}image`}
            name={`${path}image`}
            label={intl.formatMessage({ id: "common.asset.selectFile.label" })}
            onChange={handleImageChange}
          />
        )}
      </DataGridItem>
      {values && values.image && (
        <>
          <DataGridItem big>
            <FormTextField
              disabled={disabled}
              type="text"
              id={`${path}codename`}
              name={`${path}codename`}
              value={values.codename}
              onChange={onChange}
              label={intl.formatMessage({ id: "common.name" })}
            />
          </DataGridItem>
          <DataGridItem big>
            <FormTextField
              disabled={disabled}
              type="text"
              id={`${path}description`}
              name={`${path}description`}
              value={values.description}
              onChange={onChange}
              label={intl.formatMessage({ id: "common.description" })}
            />
          </DataGridItem>
        </>
      )}
    </DataGrid>
  );
};

NewAssetForm.propTypes = {
  /**
   * Path del atributo
   */
  path: PropTypes.string,
  /**
   * Indica si los campos están deshabilitados
   */
  disabled: PropTypes.bool,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  onChange: PropTypes.func,
  /**
   * Callback que se dispara con el cambio del input de imagen
   */
  onImageChange: PropTypes.func,
  /**
   * Mimetypes de video aceptados ("video/mp4")
   */
  acceptVideo: PropTypes.array,
  /**
   * Mimetypes de imagen aceptados ("image/png", "image/apng")
   */
  acceptImage: PropTypes.array,
  /**
   * Tipos de asset aceptados ("image", "video")
   */
  allowedAssetTypes: PropTypes.array,
  /**
   * Categorías por defecto para la creación del asset
   */
  defaultCategories: PropTypes.arrayOf(PropTypes.string),
  /**
   * Indica si se deshabilita y bloquea la selección de categoría
   */
  disabledCategorySelection: PropTypes.bool,
};

export default injectIntl(NewAssetForm);
